// src/hooks/useExertionVentilationCompare.js
import { useMemo } from "react";

function safeArray(a) {
  return Array.isArray(a) ? a : [];
}
function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function formatLabel(ts) {
  const d = new Date(ts);
  // e.g. "09 Dec 15:44"
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function getT0(d) {
  const t0 = safeNum(d?.decay_fit?.t0);
  if (t0 != null) return t0;
  const times = safeArray(d?.peak_times_s)
    .map((x) => safeNum(x))
    .filter((x) => x != null);
  return times.length ? times[0] : 0;
}

function buildCumulativeCurve(d) {
  const timesRaw = safeArray(d?.peak_times_s);
  const valsRaw = safeArray(d?.peak_values);
  const n = Math.min(timesRaw.length, valsRaw.length);
  if (!n) return null;

  const t0 = getT0(d);

  let running = 0;
  const pts = [];
  for (let i = 0; i < n; i++) {
    const t = safeNum(timesRaw[i]);
    const v = safeNum(valsRaw[i]);
    if (t == null || v == null) continue;

    const x = t - t0;
    if (!Number.isFinite(x) || x < 0) continue;

    running += v;
    pts.push({ x, y: running });
  }

  return pts.length ? pts : null;
}

function buildBins30s(d, binSize = 30, maxT = 300) {
  const timesRaw = safeArray(d?.peak_times_s);
  const valsRaw = safeArray(d?.peak_values);
  const n = Math.min(timesRaw.length, valsRaw.length);
  if (!n) return null;

  const t0 = getT0(d);

  const binCount = Math.ceil(maxT / binSize);
  const sums = new Array(binCount).fill(0);

  for (let i = 0; i < n; i++) {
    const t = safeNum(timesRaw[i]);
    const v = safeNum(valsRaw[i]);
    if (t == null || v == null) continue;

    const x = t - t0;
    if (!Number.isFinite(x) || x < 0) continue;

    const idx = Math.floor(x / binSize);
    if (idx >= 0 && idx < binCount) sums[idx] += v;
  }

  return sums;
}

export function useExertionVentilationCompare({
  docs = [],
  maxCompare = 8, // overlay last N
  binSize = 30,
  maxT = 300, // show first 5 minutes by default
}) {
  const picked = useMemo(() => {
    const sorted = [...(docs || [])].sort(
      (a, b) => Number(b.ts || 0) - Number(a.ts || 0),
    );
    return sorted.slice(0, maxCompare).reverse(); // oldest->newest for nicer legend ordering
  }, [docs, maxCompare]);

  const cumulativeDatasets = useMemo(() => {
    return picked
      .map((d) => {
        const pts = buildCumulativeCurve(d);
        if (!pts) return null;
        const ts = Number(d?.ts || 0);
        return {
          label: ts ? formatLabel(ts) : d?.measurementId || "Session",
          data: pts,
          // optional: thin lines for many overlays
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.2,
        };
      })
      .filter(Boolean);
  }, [picked]);

  const bins30s = useMemo(() => {
    const labels = Array.from({ length: Math.ceil(maxT / binSize) }, (_, i) => {
      const a = i * binSize;
      const b = (i + 1) * binSize;
      return `${a}-${b}s`;
    });

    const datasets = picked
      .map((d) => {
        const sums = buildBins30s(d, binSize, maxT);
        if (!sums) return null;
        const ts = Number(d?.ts || 0);
        return {
          label: ts ? formatLabel(ts) : d?.measurementId || "Session",
          data: sums, // IMPORTANT: array length must match labels
        };
      })
      .filter(Boolean);

    return { labels, datasets };
  }, [picked, binSize, maxT]);

  return { cumulativeDatasets, bins30s };
}
